import { firstTimeOfDay } from "./cron";

export { describeSchedule } from "./cron";

export const WEEKDAYS = [
  ["MON", "Mon"],
  ["TUE", "Tue"],
  ["WED", "Wed"],
  ["THU", "Thu"],
  ["FRI", "Fri"],
  ["SAT", "Sat"],
  ["SUN", "Sun"],
];

const ACCOUNT_ID = "627645292748";
const REGION = "us-east-1";
const ENV_CONFIG = {
  prod: {
    apiBase: "https://api.zenyt.ai",
    stateMachine: "zenyt-prod-agent-launch",
  },
  staging: {
    apiBase: "https://staging.api.zenyt.ai",
    stateMachine: "zenyt-staging-agent-launch",
  },
};

export function buildCron({ minute, hour, weekdays }) {
  const days = weekdays.length ? weekdays.join(",") : "*";
  return `cron(${Number(minute)} ${Number(hour)} ? * ${days} *)`;
}

export function parseEmails(value) {
  return String(value || "")
    .split(/[\n,]/)
    .map((email) => email.trim())
    .filter(Boolean);
}

export function recipientEmailsFromInput(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) return [];
  const value = input.notify_recipient_emails;
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === "string") return parseEmails(value);
  return [];
}

export function mergeRecipientEmails(existing, emailsToAdd) {
  const merged = [];
  const seen = new Set();
  for (const email of [...existing, ...parseEmails(emailsToAdd)]) {
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(email);
  }
  return merged;
}

// Schedules that share an EventBridge cron (and timezone) fire together, so the
// inventory shows them as one group. Groups are ordered by time of day; schedules
// keep the order the API returned them in.
export function groupSchedulesByCron(schedules) {
  const groups = new Map();
  for (const schedule of schedules || []) {
    const expression = schedule.schedule_expression || "";
    const timezone = schedule.schedule_expression_timezone || "";
    const key = `${timezone}|${expression}`;
    if (!groups.has(key)) {
      groups.set(key, { key, expression, timezone, schedules: [] });
    }
    groups.get(key).schedules.push(schedule);
  }
  return [...groups.values()].sort(
    (a, b) =>
      firstTimeOfDay(a.expression) - firstTimeOfDay(b.expression) ||
      a.expression.localeCompare(b.expression)
  );
}

// The launch state machine reads one of two optional audit-date fields: a fixed
// `site_date` (YYYY-MM-DD), or `site_date_offset_days`, which it turns into the
// calendar date that many days after each run starts. Both resolve to noon on the
// storefront, like the Hub's date picker. Setting both fails the execution, so
// an input carrying both is never generated.
export function siteDateFields({ siteDateOffsetDays, siteDate }) {
  const offset = String(siteDateOffsetDays ?? "").trim();
  const date = String(siteDate ?? "").trim();
  if (offset && date) return null;
  if (offset) {
    if (!/^\d{1,3}$/.test(offset)) return null;
    return { site_date_offset_days: Number(offset) };
  }
  if (date) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
    return { site_date: date };
  }
  return {};
}

export function agentSlugFromEndpoint(endpoint) {
  const match = /\/api\/v2\/agents\/([^/]+)\/run(?:[/?#]|$)/.exec(endpoint || "");
  return match ? decodeURIComponent(match[1]) : "";
}

function shellQuote(value) {
  return `'${String(value).replace(/'/g, `'"'"'`)}'`;
}

export function buildCreateScheduleCommand({
  environment,
  scheduleName,
  agentSlug,
  websiteId,
  weekdays,
  hour,
  minute,
  recipientEmails,
  siteDateOffsetDays,
  siteDate,
}) {
  if (!scheduleName || !agentSlug || !websiteId) return "";
  const auditDate = siteDateFields({ siteDateOffsetDays, siteDate });
  if (!auditDate) return "";

  const env = ENV_CONFIG[environment] || ENV_CONFIG.prod;
  const expression = buildCron({ minute, hour, weekdays });
  const input = {
    endpoint: `${env.apiBase}/api/v2/agents/${agentSlug}/run`,
    website_id: websiteId,
    notify_recipient_emails: parseEmails(recipientEmails),
    ...auditDate,
  };
  const target = {
    Arn: `arn:aws:states:${REGION}:${ACCOUNT_ID}:stateMachine:${env.stateMachine}`,
    RoleArn: `arn:aws:iam::${ACCOUNT_ID}:role/zenyt-agent-scheduler`,
    Input: JSON.stringify(input),
    RetryPolicy: {
      MaximumEventAgeInSeconds: 86400,
      MaximumRetryAttempts: 0,
    },
  };

  return [
    "aws scheduler create-schedule",
    `  --region ${REGION}`,
    `  --name ${shellQuote(scheduleName)}`,
    `  --schedule-expression ${shellQuote(expression)}`,
    `  --schedule-expression-timezone ${shellQuote("America/New_York")}`,
    "  --flexible-time-window Mode=OFF",
    "  --state ENABLED",
    `  --target ${shellQuote(JSON.stringify(target))}`,
  ].join(" \\\n");
}

export function buildAddRecipientCommand(schedule, emailsToAdd) {
  const added = parseEmails(emailsToAdd);
  const existing = recipientEmailsFromInput(schedule?.input);
  const merged = mergeRecipientEmails(existing, added);
  if (
    !schedule?.name ||
    !schedule.schedule_expression ||
    !schedule.target_arn ||
    !schedule.role_arn ||
    !schedule.input ||
    typeof schedule.input !== "object" ||
    Array.isArray(schedule.input) ||
    !added.length ||
    merged.length === existing.length
  ) {
    return "";
  }

  const target = {
    Arn: schedule.target_arn,
    RoleArn: schedule.role_arn,
    Input: JSON.stringify({
      ...schedule.input,
      notify_recipient_emails: merged,
    }),
  };
  if (schedule.retry_policy) target.RetryPolicy = schedule.retry_policy;

  return [
    "aws scheduler update-schedule",
    `  --region ${REGION}`,
    `  --name ${shellQuote(schedule.name)}`,
    schedule.group_name && schedule.group_name !== "default"
      ? `  --group-name ${shellQuote(schedule.group_name)}`
      : null,
    `  --schedule-expression ${shellQuote(schedule.schedule_expression)}`,
    schedule.schedule_expression_timezone
      ? `  --schedule-expression-timezone ${shellQuote(
          schedule.schedule_expression_timezone
        )}`
      : null,
    "  --flexible-time-window Mode=OFF",
    schedule.state ? `  --state ${schedule.state}` : null,
    `  --target ${shellQuote(JSON.stringify(target))}`,
  ]
    .filter(Boolean)
    .join(" \\\n");
}
